/* Staff rota storefront forms.
 *
 * Two custom elements, both posting to the published, secret-gated n8n
 * "Staff Portal Action" webhook carried on their data attributes:
 *   <staff-rota-request>        holiday requests (op: requestHoliday)
 *   <staff-rota-unavailability> "can't cover" blocks (op: setUnavailability)
 *
 * The server re-derives anything trust-sensitive (shift hours/dates) from the
 * rota, so the client only ever sends identifiers and the requester's id.
 */

async function postAction(url, payload) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return response.json().catch(() => ({}));
}

// Coerce a notification-preference value to on/off, matching the backend's
// default-on rule: absent/blank means on, only an explicit off value silences it.
function prefOn(value) {
  if (value === true) return true;
  if (value === false) return false;
  const s = String(value == null ? '' : value).trim().toUpperCase();
  if (s === '') return true;
  return !(s === 'FALSE' || s === 'NO' || s === '0' || s === 'OFF');
}

// Escape for safe insertion into HTML text or a double-quoted attribute.
function escapeHtml(value) {
  return String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );
}

// Three-letter weekday (as stored on recurring unavailability blocks) to the
// JS Date.getDay() index, so a weekly block can be projected onto the calendar.
const WEEKDAY_TO_DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/* ---- collapsible sections -------------------------------------------------
 * Each panel lives inside a <details data-acc> whose <summary> carries the
 * heading and an optional count badge. An element hides/opens/labels its OWN
 * enclosing section through these helpers, so no element needs to know where it
 * sits on the page. All three are no-ops if the element isn't inside a section
 * (e.g. a future layout that drops the accordion). */

function sectionOf(el) {
  return el && el.closest ? el.closest('[data-acc]') : null;
}

// Hide/show the whole section, heading included - an empty panel shouldn't leave
// a heading that opens onto nothing.
function setSectionHidden(el, hidden) {
  const section = sectionOf(el);
  if (section) section.hidden = !!hidden;
}

// Put a count in the heading ("1 awaiting cover"), so staff can see what's inside
// a closed section without opening it. Falsy text removes the badge.
function setSectionBadge(el, text) {
  const section = sectionOf(el);
  if (!section) return;
  const badge = section.querySelector('[data-acc-badge]');
  if (!badge) return;
  badge.textContent = text || '';
  badge.hidden = !text;
}

// Open a section that has something waiting, so it can't sit unseen behind a
// closed heading. Never closes one the staff member opened themselves.
function openSection(el) {
  const section = sectionOf(el);
  if (section) section.open = true;
}

// "12th", "21st", "3rd" - day-of-month with its ordinal suffix.
function ordinalDay(day) {
  const n = Number(day);
  if (!Number.isFinite(n)) return String(day);
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th'}`;
}

/* Turn a sorted list of 'YYYY-MM-DD' dates into one entry per month, with
 * consecutive days collapsed into ranges:
 *   ['2026-08-12','2026-08-13','2026-08-14','2026-08-25']
 *     -> [{ label: 'August 2026', days: '12th – 14th, 25th' }]
 * A break that spans a month end appears under both months, which is the honest
 * consequence of grouping by month (December ... 31st, then January ... 1st). */
function groupDatesByMonth(dates) {
  const months = new Map();
  dates
    .map((d) => String(d || '').slice(0, 10))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort()
    .forEach((d) => {
      const key = d.slice(0, 7);
      if (!months.has(key)) months.set(key, []);
      const list = months.get(key);
      const day = Number(d.slice(8, 10));
      if (!list.includes(day)) list.push(day);
    });

  return [...months.entries()].map(([key, days]) => {
    const [year, month] = key.split('-').map(Number);
    const runs = [];
    let start = days[0];
    let prev = days[0];
    days.slice(1).forEach((day) => {
      if (day === prev + 1) {
        prev = day;
        return;
      }
      runs.push([start, prev]);
      start = day;
      prev = day;
    });
    runs.push([start, prev]);

    return {
      label: `${MONTH_NAMES[month - 1]} ${year}`,
      days: runs.map(([a, b]) => (a === b ? ordinalDay(a) : `${ordinalDay(a)} – ${ordinalDay(b)}`)).join(', '),
    };
  });
}

// A 'YYYY-MM-DD' week-start rendered like "Mon 27 Jul"; falls back to the raw
// string if it isn't a parseable date.
function formatWeekOf(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso == null ? '' : iso);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

// A shift's clock time like "4pm" / "8:30am", read straight from the ISO
// wall-clock (ignoring any timezone offset), matching how the picker shows times.
function formatClock(iso) {
  const m = String(iso || '').match(/T(\d{2}):(\d{2})/);
  if (!m) return '';
  const h = Number(m[1]);
  const min = Number(m[2]);
  const suffix = h < 12 ? 'am' : 'pm';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return min ? `${hour}:${String(min).padStart(2, '0')}${suffix}` : `${hour}${suffix}`;
}

// "4pm–10pm" from a shift's start/end ISO strings.
function formatShiftRange(start, end) {
  const from = formatClock(start);
  const to = formatClock(end);
  if (from && to) return `${from}–${to}`;
  return from || to || '';
}

// A shift's full moment from its ISO start ref, like "Sat 3 Jan 2027, 4pm". The
// manager cover view keys shifts on this ref (the rota event's start), so this is
// how a needed shift is shown to the manager.
function formatShiftMoment(iso) {
  const day = String(iso || '').slice(0, 10);
  const date = new Date(`${day}T00:00:00`);
  const dayLabel = Number.isNaN(date.getTime())
    ? day
    : date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const clock = formatClock(iso);
  return clock ? `${dayLabel}, ${clock}` : dayLabel;
}

// A candidate's eligibility-warning reason -> a short plain-English phrase for the
// coverer picker. Mirrors the reasons the backend six-rule engine emits; a manager
// can still assign a flagged name (the warning is advisory).
function warningText(reason) {
  const map = {
    already_working_that_day: 'already working that day',
    off_that_day: 'off that day',
    unavailable: 'marked unavailable',
    not_cover_eligible: 'not cover-eligible',
    needs_supervisor: 'not a supervisor',
    is_self: 'is the person off',
    inactive: 'inactive',
  };
  return map[reason] || (reason ? String(reason).replace(/_/g, ' ') : 'not usually available');
}

/* Holiday requests via a month calendar shift-picker. Days that carry a shift are
 * highlighted; hovering shows the hours, clicking toggles selection (multi-select
 * across days). Past days and empty days are not selectable (holiday is only ever
 * requested against a rostered shift). The submit payload is unchanged: the
 * selected shifts' start identifiers (shiftStarts) go to op: requestHoliday and the
 * server re-derives hours/dates. Shift data is read from the embedded [data-shifts]
 * JSON, built in Liquid from the rota.snapshot upcomingShifts. */
class StaffRotaRequest extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.form = this.querySelector('[data-request-form]');
    this.submitButton = this.querySelector('[data-submit]');
    this.selectedLabel = this.querySelector('[data-selected]');
    this.result = this.querySelector('[data-result]');
    this.calendarEl = this.querySelector('[data-calendar]');
    this.adminPrompt = this.querySelector('[data-admin-prompt]');
    this.unpaidPrompt = this.querySelector('[data-unpaid-prompt]');
    this.unpaidWarning = this.querySelector('[data-unpaid-warning]');
    const avail = parseFloat(this.dataset.availableHoliday);
    this.availableHoliday = Number.isFinite(avail) ? avail : null;

    if (!this.form || !this.actionUrl || !this.secret || !this.calendarEl) return;

    this.selected = new Set(); // shift-start identifiers currently picked
    this.requested = new Set(); // starts already requested this session (locked out)
    this.requestedDays = new Set(); // 'YYYY-MM-DD' of those, for colouring the calendar
    this.bookedDays = new Set(); // 'YYYY-MM-DD' with holiday already confirmed
    this.indexShifts(this.readShifts());

    const now = new Date();
    this.today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.view = { y: this.today.getFullYear(), m: this.today.getMonth() };
    this.minView = { y: this.today.getFullYear(), m: this.today.getMonth() };
    this.maxView = this.lastShiftMonth() || { y: this.view.y, m: this.view.m };

    this.calendarEl.addEventListener('click', (event) => this.onCalendarClick(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    this.renderCalendar();
    this.refreshSelection();
    this.loadRequested();
  }

  // Seed the locked-out set from the server so shifts already requested in an
  // earlier visit stay locked after a reload (this.requested is otherwise empty
  // on every load), and colour the calendar: requested days yellow, confirmed
  // days blue. Both sets are keyed by DAY as well as by shift start, because a
  // requested shift is dropped from the rota snapshot - without the day keys the
  // date simply vanishes from the calendar, which reads as "you're off" while
  // cover is still being found. Non-fatal: if the call fails the day just isn't
  // pre-locked or coloured.
  async loadRequested() {
    if (!this.customerId) return;
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listMyRequests',
        customerId: this.customerId,
      });
      if (!data || data.status !== 'ok') return;

      if (Array.isArray(data.requestedShiftRefs)) {
        data.requestedShiftRefs.forEach((ref) => {
          const start = String(ref);
          this.requested.add(start);
          const day = start.slice(0, 10);
          if (/^\d{4}-\d{2}-\d{2}$/.test(day)) this.requestedDays.add(day);
        });
      }
      // bookedDates is confirmed-status holiday only; it takes precedence over
      // requestedDays, which also contains those same days once confirmed.
      if (Array.isArray(data.bookedDates)) {
        data.bookedDates.forEach((d) => {
          const day = String(d).slice(0, 10);
          if (/^\d{4}-\d{2}-\d{2}$/.test(day)) this.bookedDays.add(day);
        });
      }
      this.renderCalendar();
      this.refreshSelection();
    } catch (error) {
      /* leave the calendar unseeded; a fresh submit still locks within the session */
    }
  }

  readShifts() {
    const el = this.querySelector('[data-shifts]');
    if (!el) return [];
    try {
      const arr = JSON.parse(el.textContent || '[]');
      return Array.isArray(arr) ? arr.filter((s) => s && s.start) : [];
    } catch (error) {
      return [];
    }
  }

  // Group shifts by calendar day (YYYY-MM-DD): a day carries its start ids + total hours.
  indexShifts(shifts) {
    this.days = new Map();
    this.hoursByStart = new Map();
    shifts.forEach((s) => {
      const key = this.dayKey(s);
      if (!key) return;
      const hours = Number(s.hours) || 0;
      this.hoursByStart.set(s.start, hours);
      const entry = this.days.get(key) || { starts: [], hours: 0, ranges: [] };
      entry.starts.push(s.start);
      entry.hours += hours;
      entry.ranges.push(this.rangeLabel(s));
      this.days.set(key, entry);
    });
  }

  dayKey(shift) {
    const match = String(shift.day || shift.start || '').match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }

  lastShiftMonth() {
    let latest = null;
    this.days.forEach((_, key) => {
      if (!latest || key > latest) latest = key;
    });
    if (!latest) return null;
    const [y, m] = latest.split('-').map(Number);
    return { y, m: m - 1 };
  }

  monthIndex(view) {
    return view.y * 12 + view.m;
  }

  isoDay(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Wall-clock HH:MM from an ISO-ish datetime string, ignoring any timezone offset -
  // the shift's local time as stored is what staff should see.
  clockTime(value) {
    const match = String(value || '').match(/T(\d{2}):(\d{2})/);
    return match ? { h: Number(match[1]), min: Number(match[2]) } : null;
  }

  // "17:00" -> "5pm"; "08:30" -> "8:30am". Minutes shown only when non-zero.
  formatTime(value) {
    const t = this.clockTime(value);
    if (!t) return '';
    const suffix = t.h < 12 ? 'am' : 'pm';
    const hour = t.h % 12 === 0 ? 12 : t.h % 12;
    return t.min ? `${hour}:${String(t.min).padStart(2, '0')}${suffix}` : `${hour}${suffix}`;
  }

  rangeLabel(shift) {
    const from = this.formatTime(shift.start);
    const to = this.formatTime(shift.end);
    if (from && to) return `${from} - ${to}`;
    return from || to || '';
  }

  // A day's shift times, e.g. "5pm - 11pm" (or several joined for a split day).
  timeLabel(entry) {
    return entry.ranges.filter(Boolean).join(', ');
  }

  ariaLabel(key, entry, isSelected) {
    const date = new Date(`${key}T00:00:00`);
    const nice = Number.isNaN(date.getTime())
      ? key
      : date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
    const times = this.timeLabel(entry);
    return `${nice}${times ? `, ${times}` : ''}${isSelected ? ', selected' : ''}`;
  }

  renderCalendar() {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const { y, m } = this.view;

    const firstOfMonth = new Date(y, m, 1);
    const offset = (firstOfMonth.getDay() + 6) % 7; // shift so Monday is column 0
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const todayKey = this.isoDay(this.today);

    const atMin = this.monthIndex(this.view) <= this.monthIndex(this.minView);
    const atMax = this.monthIndex(this.view) >= this.monthIndex(this.maxView);

    let html = '<div class="staff-rota__cal-head">';
    html += `<button type="button" class="staff-rota__cal-nav" data-nav="prev" aria-label="Previous month"${atMin ? ' disabled' : ''}>&#8249;</button>`;
    html += `<span class="staff-rota__cal-title">${monthNames[m]} ${y}</span>`;
    html += `<button type="button" class="staff-rota__cal-nav" data-nav="next" aria-label="Next month"${atMax ? ' disabled' : ''}>&#8250;</button>`;
    html += '</div><div class="staff-rota__cal-grid">';

    weekdays.forEach((d) => {
      html += `<span class="staff-rota__cal-dow">${d}</span>`;
    });
    for (let i = 0; i < offset; i += 1) {
      html += '<span class="staff-rota__cal-day staff-rota__cal-day--empty" aria-hidden="true"></span>';
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const key = this.isoDay(new Date(y, m, day));
      const entry = this.days.get(key);
      const isPast = key < todayKey;

      // Holiday days first, and blue (booked) beats yellow (requested): these
      // are shown whether or not the day still carries a shift in the snapshot,
      // since a released shift is removed from it.
      if (!isPast && this.bookedDays.has(key)) {
        html += `<span class="staff-rota__cal-day staff-rota__cal-day--booked" title="Holiday booked">${day}</span>`;
        continue;
      }
      if (!isPast && this.requestedDays.has(key)) {
        html += `<span class="staff-rota__cal-day staff-rota__cal-day--requested" title="Holiday requested - not confirmed yet">${day}</span>`;
        continue;
      }

      if (entry && !isPast) {
        const allRequested = entry.starts.every((s) => this.requested.has(s));
        if (allRequested) {
          html += `<span class="staff-rota__cal-day staff-rota__cal-day--requested" title="Holiday requested - not confirmed yet">${day}</span>`;
          continue;
        }
        const isSelected = entry.starts.some((s) => this.selected.has(s));
        const cls = `staff-rota__cal-day staff-rota__cal-day--shift${isSelected ? ' staff-rota__cal-day--selected' : ''}`;
        html += `<button type="button" class="${cls}" data-day="${key}" data-times="${this.timeLabel(entry)}" aria-pressed="${isSelected}" aria-label="${this.ariaLabel(key, entry, isSelected)}">${day}</button>`;
      } else {
        const cls = `staff-rota__cal-day${isPast ? ' staff-rota__cal-day--past' : ' staff-rota__cal-day--plain'}`;
        html += `<span class="${cls}">${day}</span>`;
      }
    }

    html += '</div>';
    this.calendarEl.innerHTML = html;
  }

  onCalendarClick(event) {
    const nav = event.target.closest('[data-nav]');
    if (nav) {
      if (nav.disabled) return;
      const step = nav.dataset.nav === 'next' ? 1 : -1;
      const min = this.monthIndex(this.minView);
      const max = this.monthIndex(this.maxView);
      const next = Math.min(max, Math.max(min, this.monthIndex(this.view) + step));
      this.view = { y: Math.floor(next / 12), m: next % 12 };
      this.renderCalendar();
      return;
    }

    const cell = event.target.closest('[data-day]');
    if (!cell) return;
    const entry = this.days.get(cell.dataset.day);
    if (!entry) return;

    const isSelected = entry.starts.some((s) => this.selected.has(s));
    entry.starts.forEach((s) => {
      if (this.requested.has(s)) return;
      if (isSelected) this.selected.delete(s);
      else this.selected.add(s);
    });
    this.renderCalendar();
    this.refreshSelection();
  }

  refreshSelection() {
    const count = this.selected.size;
    let hours = 0;
    this.selected.forEach((s) => {
      hours += this.hoursByStart.get(s) || 0;
    });
    hours = Math.round(hours * 100) / 100;

    if (count === 0) {
      this.selectedLabel.textContent = 'No shifts selected';
    } else {
      const plural = count === 1 ? 'shift' : 'shifts';
      this.selectedLabel.textContent = `${count} ${plural} selected (${hours}h)`;
    }
    this.updateUnpaidWarning(count, hours);
    this.submitButton.disabled = count === 0 || this.busy === true;
  }

  // Live client-side hint: if the picked shifts total more than the staffer's
  // available holiday, flag that the excess would be unpaid leave. The server makes
  // the authoritative split on submit (balance can move); this is just a heads-up.
  updateUnpaidWarning(count, hours) {
    if (!this.unpaidWarning) return;
    if (count > 0 && this.availableHoliday != null && hours > this.availableHoliday + 1e-9) {
      this.unpaidWarning.textContent =
        `Warning: you only have ${this.availableHoliday} hours holiday available.`;
      this.unpaidWarning.hidden = false;
    } else {
      this.unpaidWarning.hidden = true;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.selected.size === 0 || this.busy) return;
    this.clearAdminPrompt();
    this.clearUnpaidPrompt();
    await this.send(Array.from(this.selected), null, null);
  }

  // Post a holiday request. adminDecisions is null on the first pass; on a later
  // pass (after the staffer answers the admin-hours prompt) it is a
  // { blockRef: 'holiday' | 'home' } map. unpaidDecision is null until the staffer
  // answers the unpaid-leave prompt, then 'permanent' | 'convert_when_able'. Both
  // are carried through so a request needing both prompts resolves in order (admin
  // first, then unpaid). The server re-derives everything else.
  async send(starts, adminDecisions, unpaidDecision) {
    this.setBusy(true);
    this.showResult('Sending your request…', 'pending');

    const payload = {
      secret: this.secret,
      operation: 'requestHoliday',
      customerId: this.customerId,
      shiftStarts: starts,
      reason: (this.form.reason.value || '').trim(),
    };
    if (adminDecisions) payload.adminDecisions = adminDecisions;
    if (unpaidDecision) payload.unpaidDecision = unpaidDecision;

    try {
      const data = await postAction(this.actionUrl, payload);
      this.handleResult(data, starts, adminDecisions);
    } catch (error) {
      this.showResult('Sorry, that didn’t send. Please check your connection and try again.', 'error');
    } finally {
      this.setBusy(false);
    }
  }

  handleResult(data, starts, adminDecisions) {
    const status = (data && data.status) || 'error';
    const message = (data && data.message) || 'Something went wrong with that request.';

    // The requester has admin hours across a full week of this request: ask, per
    // week, whether they'll still do them from home or take them as holiday too,
    // then resubmit with their answers.
    if (status === 'admin_prompt' && Array.isArray(data.adminBlocks) && data.adminBlocks.length) {
      this.showAdminPrompt(data, starts);
      return;
    }

    // The requester is short of holiday: show the paid/unpaid split and ask how the
    // unpaid shifts should be handled, then resubmit with that choice (carrying any
    // admin-hours answers from the earlier pass).
    if (status === 'unpaid_prompt' && data && data.split) {
      this.showUnpaidPrompt(data, starts, adminDecisions || null);
      return;
    }

    // A resubmit of something already on file: the server records nothing new,
    // just confirms it's still pending. Treat it as a success and lock the day.
    // Anything else (valid / needs_approval) is a genuine new recording.
    if (status === 'valid' || status === 'needs_approval' || status === 'already_requested') {
      starts.forEach((s) => {
        this.requested.add(s);
        this.selected.delete(s);
      });
      this.renderCalendar();
      this.refreshSelection();
      this.clearAdminPrompt();
      this.clearUnpaidPrompt();

      let confirmMsg = '✓ Request received. We’ll arrange cover and let you know once it’s confirmed.';
      if (status === 'needs_approval') {
        confirmMsg = '✓ Request received — this time needs manager approval, so it’s been sent to Mike. You’ll be told once he decides.';
      } else if (status === 'already_requested') {
        confirmMsg = '✓ You’ve already requested this time off — it’s still pending, so there’s nothing more to do.';
      } else {
        const unpaid = data && Number(data.unpaidHours) > 0 ? Number(data.unpaidHours) : 0;
        if (unpaid > 0) confirmMsg += ` ${unpaid}h of this will be unpaid leave.`;
      }
      // 'already_requested' shares the success styling of a valid request.
      this.showResult(confirmMsg, status === 'already_requested' ? 'valid' : status);
      return;
    }

    this.clearAdminPrompt();
    this.clearUnpaidPrompt();
    this.showResult(message, status);
  }

  // Render the per-week admin-hours question and, on confirm, resubmit with the
  // staffer's choices. Defaults to "do them from home" (no holiday deduction).
  showAdminPrompt(data, starts) {
    if (!this.adminPrompt) {
      // No container to render into (older markup): fail safe by resubmitting
      // with every block left as "home" so the request never gets stuck.
      this.send(starts, {}, null);
      return;
    }

    const intro =
      (data && data.message) ||
      'You have admin hours during this time off. For each week, are you still doing them from home, or taking them as holiday too?';

    const rows = data.adminBlocks
      .map((block, i) => {
        const ref = escapeHtml(block.blockRef);
        const group = `admin-decision-${i}`;
        return (
          `<li class="staff-rota__admin-block">` +
          `<p class="staff-rota__admin-block-label">${escapeHtml(block.hours)}h admin the week of ${escapeHtml(formatWeekOf(block.weekOf))}</p>` +
          `<label class="staff-rota__admin-choice"><input type="radio" name="${group}" value="home" data-block-ref="${ref}" checked> I’ll still do these from home</label>` +
          `<label class="staff-rota__admin-choice"><input type="radio" name="${group}" value="holiday" data-block-ref="${ref}"> Take these as holiday too</label>` +
          `</li>`
        );
      })
      .join('');

    this.adminPrompt.innerHTML =
      `<p class="staff-rota__admin-intro">${escapeHtml(intro)}</p>` +
      `<ul class="staff-rota__admin-list">${rows}</ul>` +
      `<button type="button" class="button" data-admin-confirm>Confirm and submit</button>`;
    this.adminPrompt.hidden = false;
    this.showResult('Almost there — answer the admin-hours question below to finish.', 'pending');

    this.adminPrompt.querySelector('[data-admin-confirm]').addEventListener('click', () => {
      const decisions = {};
      this.adminPrompt
        .querySelectorAll('input[type="radio"]:checked')
        .forEach((radio) => {
          decisions[radio.dataset.blockRef] = radio.value;
        });
      this.send(starts, decisions, null);
    });
  }

  clearAdminPrompt() {
    if (!this.adminPrompt) return;
    this.adminPrompt.innerHTML = '';
    this.adminPrompt.hidden = true;
  }

  // The requester is short of holiday: show the paid/unpaid split and a dropdown for
  // how to treat the unpaid shifts - take them as unpaid leave, or take them as
  // unpaid for now and switch to holiday later if enough is accrued before the shift.
  // Resubmit with that choice, carrying any admin-hours answers from the earlier pass.
  showUnpaidPrompt(data, starts, adminDecisions) {
    if (!this.unpaidPrompt) {
      // No container (older markup): fail safe by taking the shortfall as unpaid.
      this.send(starts, adminDecisions, 'permanent');
      return;
    }

    const split = data.split || {};
    const paidHours = Number(split.paidHours) || 0;
    const unpaidHours = Number(split.unpaidHours) || 0;

    // Only mention the paid-holiday portion when there is one (a staffer with zero
    // balance sees just the unpaid line).
    const holidayLine =
      paidHours > 0
        ? `<strong>${escapeHtml(paidHours)}</strong> hours will be taken as holiday. `
        : '';

    this.unpaidPrompt.innerHTML =
      `<p class="staff-rota__unpaid-split">${holidayLine}` +
      `<strong>${escapeHtml(unpaidHours)}</strong> hours will be unpaid leave.</p>` +
      `<label class="staff-rota__label" for="StaffRotaUnpaid">How do you want to handle the unpaid shifts?</label>` +
      `<select class="staff-rota__unpaid-select" id="StaffRotaUnpaid" data-unpaid-choice>` +
      `<option value="convert_when_able">Use holiday when available</option>` +
      `<option value="permanent">Keep as unpaid leave</option>` +
      `</select>` +
      `<button type="button" class="button" data-unpaid-confirm>Confirm and submit</button>`;
    this.unpaidPrompt.hidden = false;
    this.showResult('Almost there — choose how to handle the unpaid shifts below to finish.', 'pending');

    this.unpaidPrompt.querySelector('[data-unpaid-confirm]').addEventListener('click', () => {
      const choice = this.unpaidPrompt.querySelector('[data-unpaid-choice]');
      const decision = choice && choice.value === 'permanent' ? 'permanent' : 'convert_when_able';
      this.send(starts, adminDecisions, decision);
    });
  }

  clearUnpaidPrompt() {
    if (!this.unpaidPrompt) return;
    this.unpaidPrompt.innerHTML = '';
    this.unpaidPrompt.hidden = true;
  }

  setBusy(busy) {
    this.busy = busy;
    this.submitButton.classList.toggle('loading', busy);
    this.refreshSelection();
  }

  showResult(message, status) {
    this.result.textContent = message;
    this.result.hidden = false;
    this.result.dataset.status = status;
  }
}

class StaffRotaUnavailability extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.form = this.querySelector('[data-unavail-form]');
    this.submitButton = this.querySelector('[data-submit]');
    this.result = this.querySelector('[data-result]');
    this.recurringFields = this.querySelector('[data-recurring]');
    this.oneoffFields = this.querySelector('[data-oneoff]');
    this.timeFields = this.querySelector('[data-times]');
    this.period = this.querySelector('[data-period]');
    this.fromField = this.querySelector('[data-from-field]');
    this.toField = this.querySelector('[data-to-field]');

    if (!this.form || !this.actionUrl || !this.secret) return;

    this.form.querySelectorAll('input[name="kind"]').forEach((radio) =>
      radio.addEventListener('change', () => this.refreshKind()),
    );
    if (this.period) this.period.addEventListener('change', () => this.refreshTimes());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    this.refreshKind();
    this.refreshTimes();

    // Read-only calendar of already-saved blocks. Loads from the same webhook
    // (op: listUnavailability) and re-loads after each successful add.
    this.calendarEl = this.querySelector('[data-unavail-calendar]');
    if (this.calendarEl) {
      const now = new Date();
      this.today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      this.view = { y: this.today.getFullYear(), m: this.today.getMonth() };
      this.minView = { y: this.view.y, m: this.view.m };
      this.maxView = { y: this.view.y, m: this.view.m };
      this.blocks = [];
      this.calendarEl.addEventListener('click', (event) => this.onCalendarNav(event));
      this.loadBlocks();
    }
  }

  async loadBlocks() {
    this.loadError = false;
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listUnavailability',
        customerId: this.customerId,
      });
      this.blocks =
        data && data.status === 'ok' && Array.isArray(data.unavailability)
          ? data.unavailability
          : [];
    } catch (error) {
      this.loadError = true;
      this.blocks = [];
    }
    this.computeBounds();
    this.renderCalendar();
  }

  // The calendar always starts at the current month; it can page forward to at
  // least three months ahead, and further if a one-off block sits beyond that.
  // Past months are never shown - unavailability is only forward-looking here.
  computeBounds() {
    const curIdx = this.monthIndex(this.view);
    let maxIdx = curIdx + 3;
    this.blocks.forEach((b) => {
      if (b.kind === 'oneoff' && b.date) {
        const [y, m] = String(b.date).split('-').map(Number);
        if (!Number.isNaN(y) && !Number.isNaN(m)) maxIdx = Math.max(maxIdx, y * 12 + (m - 1));
      }
    });
    this.minView = { y: this.today.getFullYear(), m: this.today.getMonth() };
    this.maxView = { y: Math.floor(maxIdx / 12), m: maxIdx % 12 };
  }

  monthIndex(view) {
    return view.y * 12 + view.m;
  }

  isoDay(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // "17:00" -> "5pm"; "08:30" -> "8:30am".
  formatClock(value) {
    const match = String(value || '').match(/(\d{2}):(\d{2})/);
    if (!match) return String(value == null ? '' : value);
    const h = Number(match[1]);
    const min = match[2];
    const suffix = h < 12 ? 'am' : 'pm';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return min === '00' ? `${hour}${suffix}` : `${hour}:${min}${suffix}`;
  }

  // Which portion(s) of a given day are blocked, aggregated across every block
  // that lands on it. Morning = starts at the top of the day (not ending 23:59);
  // evening = runs to the end of the day; all-day, or both halves, fill the box.
  dayFill(y, m, day) {
    const date = new Date(y, m, day);
    const key = this.isoDay(date);
    const dow = date.getDay();
    let morning = false;
    let evening = false;
    let allday = false;
    const labels = [];

    this.blocks.forEach((b) => {
      let matches = false;
      if (b.kind === 'recurring') matches = WEEKDAY_TO_DOW[b.weekday] === dow;
      else if (b.kind === 'oneoff') matches = String(b.date || '').slice(0, 10) === key;
      if (!matches) return;

      const note = b.note ? ` (${b.note})` : '';
      if (b.allDay) {
        allday = true;
        labels.push(`All day${note}`);
      } else if (String(b.endTime) === '23:59') {
        evening = true;
        labels.push(`Evening from ${this.formatClock(b.startTime)}${note}`);
      } else {
        morning = true;
        labels.push(`Morning until ${this.formatClock(b.endTime)}${note}`);
      }
    });

    if (!morning && !evening && !allday) return null;
    let part = 'bottom';
    if (allday || (morning && evening)) part = 'full';
    else if (morning) part = 'top';
    return { part, info: labels.join('; ') };
  }

  dayAria(date, fill) {
    const nice = date.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
    const map = { top: 'morning unavailable', bottom: 'evening unavailable', full: 'unavailable all day' };
    return `${nice}, ${map[fill.part]}`;
  }

  onCalendarNav(event) {
    const nav = event.target.closest('[data-nav]');
    if (!nav || nav.disabled) return;
    const step = nav.dataset.nav === 'next' ? 1 : -1;
    const min = this.monthIndex(this.minView);
    const max = this.monthIndex(this.maxView);
    const next = Math.min(max, Math.max(min, this.monthIndex(this.view) + step));
    this.view = { y: Math.floor(next / 12), m: next % 12 };
    this.renderCalendar();
  }

  renderCalendar() {
    if (!this.calendarEl) return;
    if (this.loadError) {
      this.calendarEl.innerHTML =
        '<p class="staff-rota__form-hint">Could not load your saved unavailability. Please refresh the page.</p>';
      return;
    }

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const { y, m } = this.view;

    const firstOfMonth = new Date(y, m, 1);
    const offset = (firstOfMonth.getDay() + 6) % 7; // Monday is column 0
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const todayKey = this.isoDay(this.today);

    const atMin = this.monthIndex(this.view) <= this.monthIndex(this.minView);
    const atMax = this.monthIndex(this.view) >= this.monthIndex(this.maxView);

    let html = '<div class="staff-rota__cal-head">';
    html += `<button type="button" class="staff-rota__cal-nav" data-nav="prev" aria-label="Previous month"${atMin ? ' disabled' : ''}>&#8249;</button>`;
    html += `<span class="staff-rota__cal-title">${monthNames[m]} ${y}</span>`;
    html += `<button type="button" class="staff-rota__cal-nav" data-nav="next" aria-label="Next month"${atMax ? ' disabled' : ''}>&#8250;</button>`;
    html += '</div><div class="staff-rota__cal-grid">';

    weekdays.forEach((d) => {
      html += `<span class="staff-rota__cal-dow">${d}</span>`;
    });
    for (let i = 0; i < offset; i += 1) {
      html += '<span class="staff-rota__cal-day staff-rota__cal-day--empty" aria-hidden="true"></span>';
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(y, m, day);
      const key = this.isoDay(date);
      const isPast = key < todayKey;
      const fill = this.dayFill(y, m, day);
      const pastCls = isPast ? ' staff-rota__cal-day--past' : '';

      if (fill) {
        html += `<span class="staff-rota__cal-day staff-rota__unavail-day staff-rota__unavail-day--${fill.part}${pastCls}" data-info="${escapeHtml(fill.info)}" aria-label="${escapeHtml(this.dayAria(date, fill))}">${day}</span>`;
      } else {
        html += `<span class="staff-rota__cal-day${isPast ? ' staff-rota__cal-day--past' : ' staff-rota__cal-day--plain'}">${day}</span>`;
      }
    }

    html += '</div>';
    if (!this.blocks.length) {
      html +=
        '<p class="staff-rota__form-hint staff-rota__form-hint--below">You haven&rsquo;t marked any unavailability yet. Add one below and it&rsquo;ll show here.</p>';
    }
    this.calendarEl.innerHTML = html;
  }

  currentKind() {
    const checked = this.form.querySelector('input[name="kind"]:checked');
    return checked ? checked.value : 'recurring';
  }

  refreshKind() {
    const recurring = this.currentKind() === 'recurring';
    if (this.recurringFields) this.recurringFields.hidden = !recurring;
    if (this.oneoffFields) this.oneoffFields.hidden = recurring;
  }

  currentPeriod() {
    return this.period ? this.period.value : 'allday';
  }

  // Morning = unavailable from the start of the day up to a "To" time, so only the
  // To box shows. Evening = unavailable from a "From" time to the end of the day, so
  // only the From box shows. All day = whole day, no time boxes.
  refreshTimes() {
    const period = this.currentPeriod();
    if (this.timeFields) this.timeFields.hidden = period === 'allday';
    if (this.fromField) this.fromField.hidden = period !== 'evening';
    if (this.toField) this.toField.hidden = period !== 'morning';
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.busy) return;

    const kind = this.currentKind();
    const period = this.currentPeriod();
    const f = this.form;

    // Light client-side checks; the server validates authoritatively.
    if (kind === 'oneoff' && !f.date.value) {
      this.showResult('Please pick a date.', 'error');
      return;
    }

    // Map the chosen period to a time block. Morning blocks the start of the day up
    // to the To time; evening blocks from the From time to the end of the day; all
    // day sends no times (the server records that as an all-day block).
    const allDay = period === 'allday';
    let startTime;
    let endTime;
    if (period === 'morning') {
      startTime = '00:00';
      endTime = `${f.endHour.value}:${f.endMinute.value}`;
      if (endTime <= startTime) {
        this.showResult('Please choose a “To” time later than the start of the day.', 'error');
        return;
      }
    } else if (period === 'evening') {
      startTime = `${f.startHour.value}:${f.startMinute.value}`;
      endTime = '23:59';
      if (startTime >= endTime) {
        this.showResult('Please choose an earlier “From” time.', 'error');
        return;
      }
    }

    this.setBusy(true);
    this.showResult('Saving…', 'pending');

    const payload = {
      secret: this.secret,
      operation: 'setUnavailability',
      customerId: this.customerId,
      kind,
      weekday: kind === 'recurring' ? f.weekday.value : undefined,
      date: kind === 'oneoff' ? f.date.value : undefined,
      allDay,
      startTime: allDay ? undefined : startTime,
      endTime: allDay ? undefined : endTime,
      note: (f.note.value || '').trim(),
    };

    try {
      const data = await postAction(this.actionUrl, payload);
      const status = (data && data.status) || 'error';
      const message = (data && data.message) || 'Something went wrong saving that.';
      this.showResult(message, status === 'ok' ? 'valid' : status);
      if (status === 'ok') {
        f.note.value = '';
        if (this.calendarEl) this.loadBlocks();
      }
    } catch (error) {
      this.showResult('Sorry, that didn’t save. Please check your connection and try again.', 'error');
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.busy = busy;
    this.submitButton.disabled = busy;
    this.submitButton.classList.toggle('loading', busy);
  }

  showResult(message, status) {
    this.result.textContent = message;
    this.result.hidden = false;
    this.result.dataset.status = status;
  }
}

/* Manager admin: list/create/delete blackout + needs-approval holiday periods.
 * Only rendered for management staff (gated in Liquid on the snapshot role).
 * listPeriods on connect; createPeriod from the form; deletePeriod per row. */
class StaffRotaAdmin extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.form = this.querySelector('[data-period-form]');
    this.submitButton = this.querySelector('[data-submit]');
    this.result = this.querySelector('[data-result]');
    this.list = this.querySelector('[data-periods-list]');

    if (!this.form || !this.list || !this.actionUrl || !this.secret) return;

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.list.addEventListener('click', (event) => this.onListClick(event));
    this.loadPeriods();
  }

  async loadPeriods() {
    this.renderMessage('Loading current periods…');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listPeriods',
        customerId: this.customerId,
      });
      if (data && data.status === 'ok' && Array.isArray(data.periods)) {
        this.renderPeriods(data.periods);
      } else {
        this.renderMessage((data && data.message) || 'Could not load periods.');
      }
    } catch (error) {
      this.renderMessage('Could not load periods. Please check your connection.');
    }
  }

  renderMessage(text) {
    this.list.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'staff-rota__periods-empty';
    p.textContent = text;
    this.list.appendChild(p);
  }

  renderPeriods(periods) {
    if (!periods.length) {
      this.renderMessage('No periods set. Staff can request holiday on any date (subject to the usual rules).');
      return;
    }

    const ordered = periods
      .slice()
      .sort((a, b) => String(a.start || '').localeCompare(String(b.start || '')));

    this.list.innerHTML = '';
    const ul = document.createElement('ul');
    ul.className = 'staff-rota__periods-list';
    ul.setAttribute('role', 'list');

    ordered.forEach((period) => {
      const li = document.createElement('li');
      li.className = 'staff-rota__period';

      const type = document.createElement('span');
      const isBlackout = period.type === 'blackout';
      type.className = `staff-rota__period-type staff-rota__period-type--${isBlackout ? 'blackout' : 'approval'}`;
      type.textContent = isBlackout ? 'Blackout' : 'Needs approval';

      const range = document.createElement('span');
      range.className = 'staff-rota__period-range';
      range.textContent = this.formatRange(period.start, period.end);

      const note = document.createElement('span');
      note.className = 'staff-rota__period-note';
      note.textContent = period.note || '';

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'staff-rota__period-delete link';
      remove.textContent = 'Remove';
      remove.dataset.deletePeriod = period.id;

      li.append(type, range, note, remove);
      ul.appendChild(li);
    });

    this.list.appendChild(ul);
  }

  // Stored ends carry T23:59:59 of the inclusive last day, so the date part is
  // exactly what the manager picked - show that, not a day later.
  formatRange(start, end) {
    const from = this.formatDate(start);
    const to = this.formatDate(end);
    return from === to ? from : `${from} – ${to}`;
  }

  formatDate(value) {
    const day = String(value || '').slice(0, 10);
    const date = new Date(`${day}T00:00:00`);
    if (Number.isNaN(date.getTime())) return day;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.busy) return;

    const f = this.form;
    const startDate = f.startDate.value;
    const endDate = f.endDate.value;

    if (!startDate || !endDate) {
      this.showResult('Please set both a start and end date.', 'error');
      return;
    }
    if (endDate < startDate) {
      this.showResult('The end date can’t be before the start date.', 'error');
      return;
    }

    this.setBusy(true);
    this.showResult('Saving…', 'pending');

    const payload = {
      secret: this.secret,
      operation: 'createPeriod',
      customerId: this.customerId,
      periodType: f.periodType.value,
      startDate,
      endDate,
      note: (f.note.value || '').trim(),
    };

    try {
      const data = await postAction(this.actionUrl, payload);
      const status = (data && data.status) || 'error';
      const message = (data && data.message) || 'Something went wrong saving that.';
      this.showResult(message, status === 'ok' ? 'valid' : status);
      if (status === 'ok') {
        f.note.value = '';
        this.loadPeriods();
      }
    } catch (error) {
      this.showResult('Sorry, that didn’t save. Please check your connection and try again.', 'error');
    } finally {
      this.setBusy(false);
    }
  }

  async onListClick(event) {
    const button = event.target.closest('[data-delete-period]');
    if (!button) return;
    const periodId = button.dataset.deletePeriod;
    if (!periodId || this.busy) return;
    if (!window.confirm('Remove this period? Staff will no longer be restricted for these dates.')) return;

    this.setBusy(true);
    button.disabled = true;

    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'deletePeriod',
        customerId: this.customerId,
        periodId,
      });
      if (data && data.status === 'ok') {
        this.loadPeriods();
      } else {
        this.showResult((data && data.message) || 'Could not remove that period.', 'error');
        button.disabled = false;
      }
    } catch (error) {
      this.showResult('Sorry, that didn’t work. Please try again.', 'error');
      button.disabled = false;
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    this.busy = busy;
    if (this.submitButton) {
      this.submitButton.disabled = busy;
      this.submitButton.classList.toggle('loading', busy);
    }
  }

  showResult(message, status) {
    this.result.textContent = message;
    this.result.hidden = false;
    this.result.dataset.status = status;
  }
}

/* Cover offers: the shifts a colleague's holiday has freed, offered to this
 * staffer. listOffers on connect (always current, like the admin list); each
 * offer has an Accept (op: acceptOffer, first-wins - rewrites the rota) and a
 * Decline (op: declineOffer). After either action the list is reloaded so
 * accepted/expired offers drop off. Rendered for all staff (gated in Liquid on
 * requests_enabled), not just management. */
class StaffRotaOffers extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.result = this.querySelector('[data-result]');
    this.list = this.querySelector('[data-offers-list]');

    if (!this.list || !this.actionUrl || !this.secret) return;

    this.list.addEventListener('click', (event) => this.onListClick(event));
    this.loadOffers();
  }

  async loadOffers() {
    this.renderMessage('Checking for cover offers…');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listOffers',
        customerId: this.customerId,
      });
      if (data && data.status === 'ok' && Array.isArray(data.offers)) {
        this.renderOffers(data.offers);
      } else {
        this.renderMessage((data && data.message) || 'Could not load cover offers.');
      }
    } catch (error) {
      this.renderMessage('Could not load cover offers. Please check your connection.');
    }
  }

  renderMessage(text) {
    this.list.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'staff-rota__offers-empty';
    p.textContent = text;
    this.list.appendChild(p);
  }

  renderOffers(offers) {
    if (!offers.length) {
      setSectionBadge(this, '');
      this.renderMessage('No cover offers for you right now. If a colleague books holiday and you can cover, it will appear here.');
      return;
    }

    // An offer is time-critical (first to accept gets the shift), so this section
    // opens itself rather than waiting to be found behind a closed heading.
    setSectionBadge(this, `${offers.length} offered`);
    openSection(this);

    const ordered = offers
      .slice()
      .sort((a, b) => String(a.shiftRef || '').localeCompare(String(b.shiftRef || '')));

    this.list.innerHTML = '';
    const ul = document.createElement('ul');
    ul.className = 'staff-rota__offers-listing';
    ul.setAttribute('role', 'list');

    ordered.forEach((offer) => {
      const li = document.createElement('li');
      li.className = 'staff-rota__offer';

      const label = this.formatOfferWhen(offer);
      const note = this.purposeNote(offer);
      const fullWhen = note ? `${label} · ${note}` : label;

      const when = document.createElement('span');
      when.className = 'staff-rota__offer-when';
      when.textContent = fullWhen;

      const actions = document.createElement('span');
      actions.className = 'staff-rota__offer-actions';

      const accept = document.createElement('button');
      accept.type = 'button';
      accept.className = 'button staff-rota__offer-accept';
      accept.textContent = 'Cover this shift';
      accept.dataset.acceptOffer = offer.offerId;
      accept.dataset.when = fullWhen;

      const decline = document.createElement('button');
      decline.type = 'button';
      decline.className = 'staff-rota__offer-decline link';
      decline.textContent = 'Not this time';
      decline.dataset.declineOffer = offer.offerId;

      actions.append(accept, decline);
      li.append(when, actions);
      ul.appendChild(li);
    });

    this.list.appendChild(ul);
  }

  // Day + the SLOT's start-end, e.g. "Sat 22 Aug, 09:30-17:30". The slot is what the
  // staffer actually covers, so a store-open partial reads "Fri 21 Aug, 10:30-11:00".
  // Falls back to just the start (legacy offers with no end), then to the day alone.
  formatOfferWhen(offer) {
    const iso = String(offer.day || '').slice(0, 10);
    const date = new Date(`${iso}T00:00:00`);
    const dayLabel = Number.isNaN(date.getTime())
      ? iso
      : date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    const start = offer.startTime || '';
    const end = offer.endTime || '';
    if (start && end) return `${dayLabel}, ${start}–${end}`;
    if (start) return `${dayLabel}, ${start}`;
    return dayLabel;
  }

  // A short plain-English note for a partial slot, so staff know what they're covering:
  // the open-up half hour reads "open the store", the closing rest "close the store".
  purposeNote(offer) {
    if (offer.purpose === 'open') return 'open the store';
    if (offer.purpose === 'close') return 'close the store';
    return '';
  }

  async onListClick(event) {
    const acceptBtn = event.target.closest('[data-accept-offer]');
    const declineBtn = event.target.closest('[data-decline-offer]');
    if (acceptBtn) return this.accept(acceptBtn);
    if (declineBtn) return this.decline(declineBtn);
  }

  async accept(button) {
    const offerId = button.dataset.acceptOffer;
    if (!offerId || this.busy) return;
    const when = button.dataset.when || 'this shift';
    if (!window.confirm(`Cover ${when}? You'll be added to the rota for it.`)) return;

    this.setBusy(true, button);
    this.showResult('Confirming your cover…', 'pending');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'acceptOffer',
        customerId: this.customerId,
        offerId,
      });
      const status = (data && data.status) || 'error';
      const message = (data && data.message) || 'That offer could not be accepted.';
      this.showResult(message, status === 'ok' ? 'ok' : 'error');
      // Always refresh: on success the accepted + expired siblings drop off; on a
      // first-wins loss ("already covered") the stale offer is gone from the list too.
      this.loadOffers();
    } catch (error) {
      this.showResult('Sorry, that didn’t go through. Please try again.', 'error');
    } finally {
      this.setBusy(false, button);
    }
  }

  async decline(button) {
    const offerId = button.dataset.declineOffer;
    if (!offerId || this.busy) return;
    if (!window.confirm('Decline this cover offer? It will be offered to others.')) return;

    this.setBusy(true, button);
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'declineOffer',
        customerId: this.customerId,
        offerId,
      });
      if (data && data.status === 'ok') {
        this.loadOffers();
      } else {
        this.showResult((data && data.message) || 'Could not decline that offer.', 'error');
        this.loadOffers();
      }
    } catch (error) {
      this.showResult('Sorry, that didn’t work. Please try again.', 'error');
    } finally {
      this.setBusy(false, button);
    }
  }

  setBusy(busy, button) {
    this.busy = busy;
    if (button) {
      button.disabled = busy;
      button.classList.toggle('loading', busy);
    }
  }

  showResult(message, status) {
    if (!this.result) return;
    this.result.textContent = message;
    this.result.hidden = false;
    this.result.dataset.status = status;
  }
}

/* Email notification preferences: two per-staff toggles (cover offers, holiday
 * outcomes). getNotificationPrefs on connect fills them from the registry;
 * changing a toggle saves the full current state (setNotificationPrefs). The
 * toggles stay disabled until the current state has loaded, so a staffer never
 * saves a stale default over their real choice. Response envelope is read
 * tolerantly (status:'ok' or ok:true; prefs flat or nested) so it matches the
 * action workflow's convention. Rendered for all staff (gated in Liquid on
 * requests_enabled). */
class StaffRotaNotifications extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.form = this.querySelector('[data-notify-form]');
    this.result = this.querySelector('[data-result]');
    this.inputs = Array.from(this.querySelectorAll('[data-pref]'));

    if (!this.form || !this.actionUrl || !this.secret || !this.inputs.length) return;

    this.inputs.forEach((input) => input.addEventListener('change', () => this.save()));
    this.load();
  }

  // Pull the prefs out of either response shape, or null if the call failed.
  readPrefs(data) {
    if (!data) return null;
    const ok = data.status === 'ok' || data.ok === true;
    if (!ok) return null;
    const src = data.prefs && typeof data.prefs === 'object' ? data.prefs : data;
    const prefs = {};
    this.inputs.forEach((input) => {
      prefs[input.dataset.pref] = prefOn(src[input.dataset.pref]);
    });
    return prefs;
  }

  apply(prefs) {
    this.inputs.forEach((input) => {
      input.checked = prefs[input.dataset.pref];
    });
  }

  setEnabled(enabled) {
    this.inputs.forEach((input) => {
      input.disabled = !enabled;
    });
  }

  async load() {
    this.showResult('Loading your settings…', 'pending');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'getNotificationPrefs',
        customerId: this.customerId,
      });
      const prefs = this.readPrefs(data);
      if (!prefs) {
        this.showResult((data && data.message) || 'Could not load your notification settings.', 'error');
        return;
      }
      this.apply(prefs);
      this.setEnabled(true);
      this.clearResult();
    } catch (error) {
      this.showResult('Could not load your notification settings. Please refresh.', 'error');
    }
  }

  async save() {
    const payload = {
      secret: this.secret,
      operation: 'setNotificationPrefs',
      customerId: this.customerId,
    };
    this.inputs.forEach((input) => {
      payload[input.dataset.pref] = input.checked;
    });

    this.setEnabled(false);
    this.showResult('Saving…', 'pending');
    try {
      const data = await postAction(this.actionUrl, payload);
      const prefs = this.readPrefs(data);
      if (!prefs) {
        this.showResult((data && data.message) || 'Could not save. Please try again.', 'error');
        this.load(); // reload so the toggles reflect the server's real state
        return;
      }
      this.apply(prefs); // reflect the server-confirmed state
      this.setEnabled(true);
      this.showResult('Saved.', 'ok');
    } catch (error) {
      this.showResult('Could not save. Please try again.', 'error');
      this.setEnabled(true);
    }
  }

  showResult(message, status) {
    if (!this.result) return;
    this.result.textContent = message;
    this.result.hidden = false;
    this.result.dataset.status = status;
  }

  clearResult() {
    if (!this.result) return;
    this.result.hidden = true;
    this.result.textContent = '';
  }
}

/* Unpaid -> holiday conversions: shifts the staffer took as unpaid leave and asked
 * to convert to holiday once affordable. The nightly Snapshot flags the earliest
 * affordable one; listConvertible reads those still open. Each has a Convert (op:
 * convertToHoliday - draws the holiday and rewrites the rota to add their bracket)
 * and a Keep-unpaid (op: declineConversion). The section hides itself when there is
 * nothing to convert. Rendered for all staff (gated in Liquid on requests_enabled). */
class StaffRotaConvert extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.result = this.querySelector('[data-result]');
    this.list = this.querySelector('[data-convert-list]');

    if (!this.list || !this.actionUrl || !this.secret) return;

    this.list.addEventListener('click', (event) => this.onListClick(event));
    this.loadConvertible();
  }

  async loadConvertible() {
    this.renderMessage('Checking for shifts you can convert…');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listConvertible',
        customerId: this.customerId,
      });
      if (data && data.status === 'ok' && Array.isArray(data.convertible)) {
        this.renderConvertible(data.convertible);
      } else {
        this.renderMessage((data && data.message) || 'Could not load your convertible shifts.');
      }
    } catch (error) {
      this.renderMessage('Could not load your convertible shifts. Please check your connection.');
    }
  }

  renderMessage(text) {
    this.list.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'staff-rota__offers-empty';
    p.textContent = text;
    this.list.appendChild(p);
  }

  renderConvertible(items) {
    // Only surface this section when there is actually something to convert.
    if (!items.length) {
      setSectionHidden(this, true);
      return;
    }
    setSectionHidden(this, false);
    setSectionBadge(this, `${items.length} to convert`);

    this.list.innerHTML = '';
    const ul = document.createElement('ul');
    ul.className = 'staff-rota__offers-listing';
    ul.setAttribute('role', 'list');

    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'staff-rota__offer';

      const when = document.createElement('span');
      when.className = 'staff-rota__offer-when';
      const hoursText = item.hours ? ` (${item.hours}h)` : '';
      when.textContent = `${item.label || item.shiftRef}${hoursText}`;

      const actions = document.createElement('span');
      actions.className = 'staff-rota__offer-actions';

      const convert = document.createElement('button');
      convert.type = 'button';
      convert.className = 'button staff-rota__offer-accept';
      convert.textContent = 'Convert to holiday';
      convert.dataset.convert = item.conversionId;
      convert.dataset.when = item.label || item.shiftRef;

      const keep = document.createElement('button');
      keep.type = 'button';
      keep.className = 'staff-rota__offer-decline link';
      keep.textContent = 'Keep as unpaid';
      keep.dataset.declineConvert = item.conversionId;

      actions.append(convert, keep);
      li.append(when, actions);
      ul.appendChild(li);
    });

    this.list.appendChild(ul);
  }

  async onListClick(event) {
    const convertBtn = event.target.closest('[data-convert]');
    const keepBtn = event.target.closest('[data-decline-convert]');
    if (convertBtn) return this.convert(convertBtn);
    if (keepBtn) return this.decline(keepBtn);
  }

  async convert(button) {
    const conversionId = button.dataset.convert;
    if (!conversionId || this.busy) return;
    const when = button.dataset.when || 'this shift';
    if (!window.confirm(`Convert ${when} from unpaid leave to holiday? Your holiday balance will be used for it.`)) return;

    this.setBusy(true, button);
    this.showResult('Converting to holiday…', 'pending');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'convertToHoliday',
        customerId: this.customerId,
        conversionId,
      });
      const status = (data && data.status) || 'error';
      const message = (data && data.message) || 'That shift could not be converted.';
      this.showResult(message, status === 'ok' ? 'ok' : 'error');
      this.loadConvertible();
    } catch (error) {
      this.showResult('Sorry, that didn’t go through. Please try again.', 'error');
    } finally {
      this.setBusy(false, button);
    }
  }

  async decline(button) {
    const conversionId = button.dataset.declineConvert;
    if (!conversionId || this.busy) return;
    if (!window.confirm('Keep this shift as unpaid leave? We won’t offer to convert it again.')) return;

    this.setBusy(true, button);
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'declineConversion',
        customerId: this.customerId,
        conversionId,
      });
      if (data && data.status === 'ok') {
        this.loadConvertible();
      } else {
        this.showResult((data && data.message) || 'Could not update that shift.', 'error');
        this.loadConvertible();
      }
    } catch (error) {
      this.showResult('Sorry, that didn’t work. Please try again.', 'error');
    } finally {
      this.setBusy(false, button);
    }
  }

  setBusy(busy, button) {
    this.busy = busy;
    if (button) {
      button.disabled = busy;
      button.classList.toggle('loading', busy);
    }
  }

  showResult(message, status) {
    if (!this.result) return;
    this.result.textContent = message;
    this.result.hidden = false;
    this.result.dataset.status = status;
  }
}

/* Holiday awaiting cover: the staffer's own active holiday requests (op:
 * listMyRequests, blocks[]), grouped by request block, showing per-shift cover
 * progress - green once a shift's cover is accepted, red while it is still open.
 * A block leaves the list the moment every shift is covered: the server marks it
 * confirmed and stops returning it (and the holiday-outcome email tells them).
 * A needs-approval block shows amber, pending the manager's decision. The whole
 * section hides itself when nothing is awaiting. Rendered for all staff
 * (gated in Liquid on requests_enabled). */
class StaffRotaProgress extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.list = this.querySelector('[data-progress-list]');
    if (!this.list || !this.actionUrl || !this.secret) return;

    // End-times aren't stored server-side, so map each shift start -> end from the
    // rota snapshot the request form already embeds; used only to show "4pm–10pm"
    // (falls back to just the start time if a shift isn't in the snapshot).
    this.endByStart = this.readSnapshotEnds();

    this.load();
  }

  readSnapshotEnds() {
    const map = {};
    const el = document.querySelector('[data-shifts]');
    if (!el) return map;
    try {
      const arr = JSON.parse(el.textContent || '[]');
      if (Array.isArray(arr)) {
        arr.forEach((s) => {
          if (s && s.start) map[String(s.start)] = s.end;
        });
      }
    } catch (error) {
      /* no snapshot shifts to map; time ranges fall back to start-only */
    }
    return map;
  }

  async load() {
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listMyRequests',
        customerId: this.customerId,
      });
      if (data && data.status === 'ok' && Array.isArray(data.blocks)) {
        this.render(data.blocks);
      } else {
        setSectionHidden(this, true);
      }
    } catch (error) {
      setSectionHidden(this, true); // non-fatal: just don't show the requests section
    }
  }

  render(blocks) {
    if (!blocks.length) {
      setSectionHidden(this, true);
      return;
    }
    setSectionHidden(this, false);

    // Count the shifts still without cover, so the heading says what's inside.
    const awaiting = blocks.reduce(
      (total, block) =>
        total + (Array.isArray(block.shifts) ? block.shifts.filter((s) => !s.covered).length : 0),
      0,
    );
    setSectionBadge(this, awaiting ? `${awaiting} awaiting cover` : '');

    this.list.innerHTML = '';

    blocks.forEach((block) => {
      const shifts = Array.isArray(block.shifts) ? block.shifts : [];
      const needsApproval = block.status === 'needs_approval';

      const card = document.createElement('div');
      card.className = 'staff-rota__progress-block';

      const head = document.createElement('div');
      head.className = 'staff-rota__progress-block-head';

      const title = document.createElement('span');
      title.className = 'staff-rota__progress-block-title';
      title.textContent = this.blockTitle(shifts);

      const status = document.createElement('span');
      status.className = 'staff-rota__progress-block-status';
      const outstanding = shifts.filter((s) => !s.covered).length;
      if (needsApproval) {
        status.textContent = 'Awaiting manager approval';
        status.dataset.state = 'approval';
      } else {
        // The old label was "2 of 3 covered", which never said whether the
        // holiday was actually agreed - the question staff kept asking.
        status.textContent = outstanding ? 'Not confirmed yet' : 'Covered';
        status.dataset.state = outstanding ? 'awaiting' : 'covered';
      }
      head.append(title, status);

      // Spell out what's outstanding, and that they're still rostered until it's
      // covered - the calendar alone can't say that.
      const note = document.createElement('p');
      note.className = 'staff-rota__progress-block-note';
      if (needsApproval) {
        note.textContent = 'A manager needs to approve this before cover is arranged.';
      } else if (outstanding) {
        const escalated = shifts.filter((s) => !s.covered && s.escalated).length;
        note.textContent =
          `${outstanding} of your ${shifts.length} shift${shifts.length === 1 ? '' : 's'} still needs someone to cover ` +
          `${outstanding === 1 ? 'it' : 'them'}. Until ${outstanding === 1 ? 'it does' : 'they do'}, you are still down to work these days.` +
          (escalated
            ? ` Nobody was available for ${escalated === outstanding ? (escalated === 1 ? 'it' : 'them') : `${escalated} of them`}, so a manager has been asked to arrange cover.`
            : '');
      } else {
        note.textContent = 'Every shift is covered. This booking is being confirmed.';
      }

      const ul = document.createElement('ul');
      ul.className = 'staff-rota__progress-shifts';
      ul.setAttribute('role', 'list');

      shifts.forEach((s) => {
        // 'escalated' means Mike has actually been emailed about this shift because
        // cover couldn't be found - a different thing from simply still waiting, and
        // worth saying so the staff member knows it's with a manager rather than
        // sitting in a queue. Covered always wins; escalation only qualifies a shift
        // that is still uncovered.
        const state = needsApproval
          ? 'approval'
          : s.covered
            ? 'covered'
            : s.escalated
              ? 'escalated'
              : 'awaiting';
        const li = document.createElement('li');
        li.className = 'staff-rota__progress-shift';
        li.dataset.state = state;

        const when = document.createElement('span');
        when.className = 'staff-rota__progress-shift-when';
        const range = formatShiftRange(s.start, this.endByStart[s.shiftRef] || this.endByStart[s.start]);
        when.textContent = `${formatWeekOf(s.day)}${range ? `, ${range}` : ''}`;

        const tag = document.createElement('span');
        tag.className = 'staff-rota__progress-shift-tag';
        tag.textContent =
          state === 'covered'
            ? 'Covered'
            : state === 'approval'
              ? 'Awaiting approval'
              : state === 'escalated'
                ? 'Escalated to manager'
                : 'Awaiting cover';

        li.append(when, tag);
        ul.appendChild(li);
      });

      card.append(head, note, ul);
      this.list.appendChild(card);
    });
  }

  // A block header from its shifts' days: a single day, or a "first – last" range.
  blockTitle(shifts) {
    const days = shifts.map((s) => s.day).filter(Boolean).sort();
    if (!days.length) return 'Holiday request';
    const first = days[0];
    const last = days[days.length - 1];
    return first === last ? formatWeekOf(first) : `${formatWeekOf(first)} – ${formatWeekOf(last)}`;
  }
}

/* Holiday balance headline (op: listMyRequests -> holiday).
 *
 * The payroll sheet only deducts holiday once the shift has happened, so its
 * balance silently ignores everything already booked - staff were reading it as
 * "what I can still book" and asking whether their bookings were included. This
 * shows what's genuinely left, with the two commitments broken out beneath.
 *
 * Falls back to the raw snapshot figure (labelled honestly) only if the call
 * fails, so a staffer is never left with no number at all. */
class StaffRotaBalance extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.sum = this.querySelector('[data-balance-sum]');
    if (!this.sum || !this.actionUrl || !this.secret) return;

    const raw = parseFloat(this.dataset.availableHoliday);
    this.fallback = Number.isFinite(raw) ? raw : null;

    this.load();
  }

  async load() {
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listMyRequests',
        customerId: this.customerId,
      });
      const holiday = data && data.status === 'ok' ? data.holiday : null;
      if (holiday && holiday.unlimited) {
        // Management with untracked holiday (Amy). A sum would be meaningless and
        // the raw fallback figure would be worse - it would imply a limit.
        this.renderUnlimited();
        return;
      }
      if (holiday && holiday.available != null) {
        this.render(holiday);
        return;
      }
      this.renderFallback();
    } catch (error) {
      this.renderFallback();
    }
  }

  // Trim a trailing .0 so whole numbers read cleanly; 7.5 stays 7.5.
  static num(n) {
    return String(Math.round(Number(n) * 100) / 100);
  }

  // Empty the sum but keep its caption - that caption is also what's on screen
  // while the figures are being fetched.
  clearSum() {
    const label = this.sum.querySelector('.staff-rota__balance-label');
    this.sum.innerHTML = '';
    if (label) this.sum.appendChild(label);
  }

  // One line of the sum: a signed figure, then what it is. Written as a
  // subtraction staff can follow down the page rather than a bare total.
  row(sign, value, label, modifier) {
    const cls = `staff-rota__sum-row${modifier ? ` staff-rota__sum-row--${modifier}` : ''}`;

    const signEl = document.createElement('span');
    signEl.className = `${cls} staff-rota__sum-sign`;
    signEl.textContent = sign;

    const numEl = document.createElement('span');
    numEl.className = `${cls} staff-rota__sum-num`;
    numEl.textContent = StaffRotaBalance.num(value);

    const labelEl = document.createElement('span');
    labelEl.className = `${cls} staff-rota__sum-label`;
    labelEl.textContent = `${Math.abs(Number(value)) === 1 ? 'hour' : 'hours'} ${label}`;

    this.sum.append(signEl, numEl, labelEl);
  }

  rule() {
    const el = document.createElement('span');
    el.className = 'staff-rota__sum-rule';
    this.sum.appendChild(el);
  }

  render(holiday) {
    const available = Number(holiday.available) || 0;
    const booked = Number(holiday.bookedFutureHours) || 0;
    const requested = Number(holiday.requestedPendingHours) || 0;
    const remaining = Number(holiday.remaining);

    this.clearSum();

    // Nothing committed: the sum would be "40 - 0 - 0 = 40", which is noise.
    if (!booked && !requested) {
      this.row('', remaining, 'available', 'total');
      return;
    }

    this.row('', available, 'accrued');
    if (booked) this.row('−', booked, 'booked');
    if (requested) this.row('−', requested, 'requested');
    this.rule();
    this.row('', remaining, 'available', remaining < 0 ? 'total staff-rota__sum-row--negative' : 'total');

    // Booking beyond what's accrued is possible, so say so rather than leaving a
    // bare negative number to be puzzled over.
    if (remaining < 0) {
      const note = document.createElement('span');
      note.className = 'staff-rota__sum-note';
      note.textContent = 'That is more than you have accrued - speak to a manager.';
      this.sum.appendChild(note);
    }
  }

  renderUnlimited() {
    this.clearSum();
    const el = document.createElement('span');
    el.className = 'staff-rota__sum-note staff-rota__sum-note--plain';
    el.textContent = 'Your holiday isn’t tracked against a balance.';
    this.sum.appendChild(el);
  }

  // Only when the call fails: the raw payroll figure, labelled for what it is so
  // nobody mistakes it for what's left after their bookings.
  renderFallback() {
    this.clearSum();
    if (this.fallback == null) {
      const el = document.createElement('span');
      el.className = 'staff-rota__sum-note';
      el.textContent = 'Holiday balance unavailable just now.';
      this.sum.appendChild(el);
      return;
    }
    this.row('', this.fallback, 'accrued', 'total');
    const note = document.createElement('span');
    note.className = 'staff-rota__sum-note';
    note.textContent = 'Holiday you have already booked is not taken off this figure.';
    this.sum.appendChild(note);
  }
}

/* Booked holiday: the staffer's agreed future holiday (op: listMyRequests ->
 * bookedDates, confirmed-status requests only), grouped by month with runs of
 * consecutive days collapsed into ranges. Deliberately dates only - which shift
 * was released stops mattering once the holiday is agreed. Hides its section
 * when there's nothing booked. */
class StaffRotaBooked extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.list = this.querySelector('[data-booked-list]');
    if (!this.list || !this.actionUrl || !this.secret) return;

    this.load();
  }

  async load() {
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listMyRequests',
        customerId: this.customerId,
      });
      if (data && data.status === 'ok' && Array.isArray(data.bookedDates)) {
        this.render(data.bookedDates);
      } else {
        setSectionHidden(this, true);
      }
    } catch (error) {
      setSectionHidden(this, true); // non-fatal: just don't show the booked section
    }
  }

  render(dates) {
    const months = groupDatesByMonth(dates);
    if (!months.length) {
      setSectionHidden(this, true);
      return;
    }
    setSectionHidden(this, false);

    const dayCount = new Set(dates.map((d) => String(d).slice(0, 10))).size;
    setSectionBadge(this, `${dayCount} booked`);

    this.list.innerHTML = '';
    months.forEach((month) => {
      const row = document.createElement('div');
      row.className = 'staff-rota__booked-month';

      const label = document.createElement('span');
      label.className = 'staff-rota__booked-month-name';
      label.textContent = month.label;

      const days = document.createElement('span');
      days.className = 'staff-rota__booked-month-days';
      days.textContent = month.days;

      row.append(label, days);
      this.list.appendChild(row);
    });
  }
}

/* Manager cover needs: every LIVE holiday request with its per-shift cover
 * status (op: listCoverNeeds, management-gated), and for each shift the manager
 * can act on, a coverer picker + Assign (op: assignCover) that rewrites the rota
 * to "Coverer (Person)" exactly as a staff accept would. Sits alongside the
 * periods admin panel; only rendered when snapshot.role == 'management'.
 *
 * The backend attaches an `eligible:[{label,email,eligible,warning}]` pool to
 * every needy shift (needs_assign or awaiting), eligible names first. A manager
 * may still assign a flagged (ineligible) name - the warning is advisory and the
 * override is recorded in the audit note. Covered/resolved/needs_approval shifts
 * carry no picker: covered shows the coverer, needs_approval is the manager's own
 * approval queue (informational here), resolved needs nothing.
 */
class StaffRotaCoverNeeds extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.result = this.querySelector('[data-result]');
    this.list = this.querySelector('[data-cover-needs-list]');
    this.filterRow = this.querySelector('[data-cn-filter-row]');
    this.filterInput = this.querySelector('[data-cn-filter]');
    this.filterCount = this.querySelector('[data-cn-filter-count]');

    if (!this.list || !this.actionUrl || !this.secret) return;

    this.requests = [];
    this.needsOnly = false;

    this.list.addEventListener('click', (event) => this.onListClick(event));
    if (this.filterInput) {
      this.filterInput.addEventListener('change', () => {
        this.needsOnly = this.filterInput.checked;
        this.render();
      });
    }
    this.loadNeeds();
  }

  async loadNeeds() {
    this.renderMessage('Loading cover needs…');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listCoverNeeds',
        customerId: this.customerId,
      });
      if (data && data.status === 'ok' && Array.isArray(data.requests)) {
        this.requests = data.requests;
        this.render();
      } else {
        this.renderMessage((data && data.message) || 'Could not load cover needs.');
      }
    } catch (error) {
      this.renderMessage('Could not load cover needs. Please check your connection.');
    }
  }

  renderMessage(text) {
    this.list.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'staff-rota__periods-empty';
    p.textContent = text;
    this.list.appendChild(p);
  }

  // Does this request need the manager to DO something? `needsAttention` is
  // computed server-side as needs_assign + needs_approval - a shift nobody is
  // lined up for, or a request waiting on his approval. A shift whose offer is
  // still out (awaiting) does NOT count: staff may yet accept it, and nothing is
  // asked of him until that runs out.
  needsAction(req) {
    return Number(req && req.needsAttention) > 0;
  }

  // A request row IS one booking block (the backend writes one HolidayRequests row
  // per connected block of shifts), so filtering by request filters block by block.
  // A block stays on screen WHOLE if any single shift in it needs action - its
  // covered and still-offered shifts shown alongside, because that surrounding
  // context is what tells him whether the block is nearly sorted or barely started.
  render() {
    const all = this.requests || [];
    const needing = all.filter((req) => this.needsAction(req));
    const shown = this.needsOnly ? needing : all;

    if (this.filterRow) this.filterRow.hidden = all.length === 0;
    if (this.filterCount) {
      this.filterCount.textContent = needing.length
        ? `${needing.length} of ${all.length} need${needing.length === 1 ? 's' : ''} action`
        : 'Nothing needs action';
    }
    setSectionBadge(this, needing.length ? `${needing.length} need${needing.length === 1 ? 's' : ''} action` : '');

    if (!all.length) {
      this.renderMessage('No holiday requests are waiting for cover right now.');
      return;
    }
    if (!shown.length) {
      this.renderMessage('Nothing needs you right now — every request is either covered or still out with staff.');
      return;
    }
    this.renderNeeds(shown);
  }

  renderNeeds(requests) {
    this.list.innerHTML = '';
    const container = document.createElement('div');
    container.className = 'staff-rota__cn-requests';

    requests.forEach((req) => container.appendChild(this.renderRequest(req)));
    this.list.appendChild(container);
  }

  renderRequest(req) {
    const card = document.createElement('section');
    card.className = 'staff-rota__cn-request';

    const head = document.createElement('div');
    head.className = 'staff-rota__cn-request-head';

    const who = document.createElement('span');
    who.className = 'staff-rota__cn-request-who';
    who.textContent = req.staffLabel || req.staffEmail || 'Staff member';

    const summary = document.createElement('span');
    summary.className = 'staff-rota__cn-request-summary';
    summary.textContent = this.summaryText(req);

    head.append(who, summary);
    card.appendChild(head);

    const ul = document.createElement('ul');
    ul.className = 'staff-rota__cn-shifts';
    ul.setAttribute('role', 'list');
    (req.shifts || []).forEach((shift) => ul.appendChild(this.renderShift(req, shift)));
    card.appendChild(ul);

    return card;
  }

  // A short "N shifts, M need cover" / "awaiting your approval" line.
  summaryText(req) {
    if (req.status === 'needs_approval') return 'Awaiting your approval';
    const total = (req.shifts || []).length;
    const need = (req.counts && req.counts.needs_assign) || 0;
    const covered = (req.counts && req.counts.covered) || 0;
    const shiftWord = total === 1 ? 'shift' : 'shifts';
    if (need > 0) return `${total} ${shiftWord} · ${need} need${need === 1 ? 's' : ''} cover`;
    if (covered === total && total > 0) return `${total} ${shiftWord} · all covered`;
    return `${total} ${shiftWord}`;
  }

  renderShift(req, shift) {
    const li = document.createElement('li');
    li.className = 'staff-rota__cn-shift';
    li.dataset.state = shift.status;

    const when = document.createElement('span');
    when.className = 'staff-rota__cn-shift-when';
    when.textContent = formatShiftMoment(shift.shiftRef);

    const tag = document.createElement('span');
    tag.className = 'staff-rota__cn-shift-tag';
    tag.textContent = this.statusLabel(shift);

    li.append(when, tag);

    // A picker + Assign only where the manager can act (needs_assign or awaiting).
    if (shift.status === 'needs_assign' || shift.status === 'awaiting') {
      li.appendChild(this.renderAssign(req, shift));
    }

    return li;
  }

  statusLabel(shift) {
    switch (shift.status) {
      case 'covered':
        return shift.coverer ? `Covered by ${shift.coverer}` : 'Covered';
      case 'awaiting':
        return 'Offer out';
      case 'needs_assign':
        return 'Needs cover';
      case 'needs_approval':
        return 'Awaiting approval';
      case 'resolved':
        return 'No cover needed';
      default:
        return shift.status || '';
    }
  }

  renderAssign(req, shift) {
    const wrap = document.createElement('div');
    wrap.className = 'staff-rota__cn-assign';

    const select = document.createElement('select');
    select.className = 'staff-rota__input staff-rota__cn-picker';
    select.setAttribute('aria-label', 'Choose someone to cover this shift');

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Choose a coverer…';
    select.appendChild(placeholder);

    // Anyone unsuitable is greyed and carries the reason, but stays SELECTABLE -
    // a manager overriding with eyes open is a real case (the override is recorded
    // in the audit note), so `disabled` would be wrong.
    //
    // Deliberately a flat list: the backend already sorts everyone who can cover to
    // the top, so the order plus the greying carries the split without headings
    // (Jonathan, 29/07/2026 - he asked for the group titles to go). Where colour
    // can't be shown at all (iOS ignores <option> colour), the "— reason" suffix
    // still says why each greyed name isn't free.
    (shift.eligible || []).forEach((cand) => {
      const opt = document.createElement('option');
      opt.value = cand.email;
      opt.textContent = cand.eligible
        ? cand.label
        : `${cand.label} — ${warningText(cand.warning)}`;
      if (!cand.eligible) opt.className = 'staff-rota__cn-option--muted';
      select.appendChild(opt);
    });

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button staff-rota__cn-assign-button';
    button.textContent = 'Assign';
    button.dataset.assign = '1';
    button.dataset.requestId = req.requestId;
    button.dataset.shiftRef = shift.shiftRef;
    button.dataset.when = formatShiftMoment(shift.shiftRef);

    wrap.append(select, button);
    return wrap;
  }

  async onListClick(event) {
    const button = event.target.closest('[data-assign]');
    if (!button || this.busy) return;

    const select = button.parentElement.querySelector('.staff-rota__cn-picker');
    const covererEmail = select ? select.value : '';
    const covererLabel = select && select.selectedOptions[0] ? select.selectedOptions[0].textContent : '';
    if (!covererEmail) {
      this.showResult('Choose someone to cover that shift first.', 'error');
      return;
    }

    const when = button.dataset.when || 'that shift';
    if (!window.confirm(`Assign ${covererLabel.replace(/ — .*/, '')} to cover ${when}? They'll be added to the rota for it.`)) return;

    this.setBusy(true, button);
    this.showResult('Assigning cover…', 'pending');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'assignCover',
        customerId: this.customerId,
        requestId: button.dataset.requestId,
        shiftRef: button.dataset.shiftRef,
        covererEmail,
      });
      const status = (data && data.status) || 'error';
      const message = (data && data.message)
        || (status === 'already_covered' ? 'That shift has already been covered.' : 'That assignment could not be saved.');
      this.showResult(message, status === 'ok' ? 'ok' : 'error');
      // Always refresh: on success the shift flips to covered; on an
      // already-covered race the stale row updates to its true state too.
      this.loadNeeds();
    } catch (error) {
      this.showResult('Sorry, that didn’t go through. Please try again.', 'error');
    } finally {
      this.setBusy(false, button);
    }
  }

  setBusy(busy, button) {
    this.busy = busy;
    if (button) {
      button.disabled = busy;
      button.classList.toggle('loading', busy);
    }
  }

  showResult(message, status) {
    if (!this.result) return;
    this.result.textContent = message;
    this.result.hidden = false;
    this.result.dataset.status = status;
  }
}

/* Manager exceptions: a read-only awareness list of where the normal holiday/cover
 * rules don't apply (op: listExceptions, management-gated). Currently the two
 * unlimited-holiday-manager (Amy) cases: her auto-approved/untracked holiday, and
 * her Thu/Fri open needing a supervisor. General "can't find cover" escalations are
 * NOT here — they reach Mike by email + the cover-needs list. Sits in the
 * management area; only rendered when snapshot.role == 'management'.
 */
class StaffRotaExceptions extends HTMLElement {
  connectedCallback() {
    this.actionUrl = this.dataset.actionUrl;
    this.secret = this.dataset.secret;
    this.customerId = this.dataset.customerId;

    this.result = this.querySelector('[data-result]');
    this.list = this.querySelector('[data-exceptions-list]');

    if (!this.list || !this.actionUrl || !this.secret) return;

    this.loadExceptions();
  }

  async loadExceptions() {
    this.renderMessage('Loading exceptions…');
    try {
      const data = await postAction(this.actionUrl, {
        secret: this.secret,
        operation: 'listExceptions',
        customerId: this.customerId,
      });
      if (data && data.status === 'ok' && Array.isArray(data.exceptions)) {
        this.renderExceptions(data.exceptions);
      } else {
        this.renderMessage((data && data.message) || 'Could not load exceptions.');
      }
    } catch (error) {
      this.renderMessage('Could not load exceptions. Please check your connection.');
    }
  }

  renderMessage(text) {
    this.list.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'staff-rota__periods-empty';
    p.textContent = text;
    this.list.appendChild(p);
  }

  renderExceptions(exceptions) {
    if (!exceptions.length) {
      this.renderMessage('No exceptions right now — everything is following the normal rules.');
      return;
    }
    this.list.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'staff-rota__exceptions-cards';
    exceptions.forEach((ex) => wrap.appendChild(this.renderException(ex)));
    this.list.appendChild(wrap);
  }

  renderException(ex) {
    const card = document.createElement('section');
    card.className = 'staff-rota__exception';

    const title = document.createElement('h3');
    title.className = 'staff-rota__exception-title';
    title.textContent = ex.title || '';
    card.appendChild(title);

    if (ex.detail) {
      const detail = document.createElement('p');
      detail.className = 'staff-rota__exception-detail';
      detail.textContent = ex.detail;
      card.appendChild(detail);
    }

    if (ex.kind === 'auto_approved_untracked' && Array.isArray(ex.requests)) {
      const ul = document.createElement('ul');
      ul.className = 'staff-rota__exception-items';
      ul.setAttribute('role', 'list');
      ex.requests.forEach((req) => {
        const li = document.createElement('li');
        li.className = 'staff-rota__exception-item';
        li.textContent = this.formatDays(req.days, req.rangeStart, req.rangeEnd);
        ul.appendChild(li);
      });
      card.appendChild(ul);
    }

    if (ex.kind === 'opener_open_needs_supervisor' && Array.isArray(ex.openSlots)) {
      const ul = document.createElement('ul');
      ul.className = 'staff-rota__exception-items';
      ul.setAttribute('role', 'list');
      ex.openSlots.forEach((slot) => {
        const li = document.createElement('li');
        li.className = 'staff-rota__exception-item staff-rota__exception-slot';
        li.dataset.state = slot.state;

        const when = document.createElement('span');
        when.className = 'staff-rota__exception-when';
        const times = slot.startTime && slot.endTime ? `, ${slot.startTime}–${slot.endTime}` : '';
        when.textContent = `${slot.day}${times}`;

        const tag = document.createElement('span');
        tag.className = 'staff-rota__exception-tag';
        tag.textContent = this.slotStateLabel(slot);

        li.append(when, tag);
        ul.appendChild(li);
      });
      card.appendChild(ul);
    }

    return card;
  }

  slotStateLabel(slot) {
    if (slot.state === 'covered') return slot.coveredBy ? `Open covered by ${slot.coveredBy}` : 'Open covered';
    if (slot.state === 'awaiting') return 'Awaiting a supervisor';
    return 'Escalated — needs you';
  }

  // "Fri 21 Aug", or "Fri 21 Aug – Sat 22 Aug" across a range, from the days array
  // (falling back to the request's range when days are absent).
  formatDays(days, rangeStart, rangeEnd) {
    const list = Array.isArray(days) ? days.filter(Boolean) : [];
    if (list.length) {
      const first = this.formatDay(list[0]);
      const last = this.formatDay(list[list.length - 1]);
      return first === last ? first : `${first} – ${last}`;
    }
    const s = this.formatDay(String(rangeStart || '').slice(0, 10));
    const e = this.formatDay(String(rangeEnd || '').slice(0, 10));
    return s === e ? s : `${s} – ${e}`;
  }

  formatDay(iso) {
    const day = String(iso || '').slice(0, 10);
    const date = new Date(`${day}T00:00:00`);
    if (Number.isNaN(date.getTime())) return day;
    return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  }
}

if (!customElements.get('staff-rota-request')) {
  customElements.define('staff-rota-request', StaffRotaRequest);
}
if (!customElements.get('staff-rota-progress')) {
  customElements.define('staff-rota-progress', StaffRotaProgress);
}

if (!customElements.get('staff-rota-balance')) {
  customElements.define('staff-rota-balance', StaffRotaBalance);
}

if (!customElements.get('staff-rota-booked')) {
  customElements.define('staff-rota-booked', StaffRotaBooked);
}
if (!customElements.get('staff-rota-convert')) {
  customElements.define('staff-rota-convert', StaffRotaConvert);
}
if (!customElements.get('staff-rota-unavailability')) {
  customElements.define('staff-rota-unavailability', StaffRotaUnavailability);
}
if (!customElements.get('staff-rota-admin')) {
  customElements.define('staff-rota-admin', StaffRotaAdmin);
}
if (!customElements.get('staff-rota-offers')) {
  customElements.define('staff-rota-offers', StaffRotaOffers);
}
if (!customElements.get('staff-rota-notifications')) {
  customElements.define('staff-rota-notifications', StaffRotaNotifications);
}
if (!customElements.get('staff-rota-cover-needs')) {
  customElements.define('staff-rota-cover-needs', StaffRotaCoverNeeds);
}
if (!customElements.get('staff-rota-exceptions')) {
  customElements.define('staff-rota-exceptions', StaffRotaExceptions);
}
